import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaHeartbeat, FaMicrochip, FaMobileAlt, FaCloud, FaShieldAlt, FaRegCheckCircle,
    FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import heroImage from '../../assets/download (3).png';

const MedicalDevicesPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaHeartbeat, title: 'Connected Health', desc: 'Secure IoT solutions for patient monitoring and data collection.', image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80' },
        { icon: FaMicrochip, title: 'Embedded Software', desc: 'Firmware development for strictly regulated medical devices (IEC 62304).', image: 'https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?w=800&q=80' },
        { icon: FaMobileAlt, title: 'SaMD Development', desc: 'Software as a Medical Device (SaMD) tailored for mobile and cloud.', image: 'https://images.unsplash.com/photo-1576091160550-21876ce7234f?w=800&q=80' },
        { icon: FaCloud, title: 'Medical Cloud', desc: 'HIPAA-compliant cloud backends for device data aggregation.', image: 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=800&q=80' },
        { icon: FaShieldAlt, title: 'Cybersecurity', desc: 'End-to-end security audits to prevent device tampering.', image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800&q=80' },
        { icon: FaRegCheckCircle, title: 'V&V Services', desc: 'Automated verification and validation for faster FDA approval.', image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80' }
    ];

    const faqs = [
        { q: 'Do you follow IEC 62304 standards?', a: 'Yes, our development process is fully aligned with IEC 62304 for medical device software life cycle processes.' },
        { q: 'Can you help with FDA 510(k) submissions?', a: 'We provide comprehensive technical documentation and validation reports to support your 510(k) submission.' },
        { q: 'How do you handle patient data privacy?', a: 'We engineer solutions with privacy-by-design, strictly adhering to HIPAA, GDPR, and other regional data protection regulations.' },
        { q: 'Do you work on Class III devices?', a: 'Yes, we have experience developing software for Class II and Class III high-risk medical devices.' },
        { q: 'What about cybersecurity for legacy devices?', a: 'We offer retrofitting services to harden legacy devices against modern cybersecurity threats.' },
        { q: 'Do you support IoMT integration?', a: 'Absolutely, we specialize in the Internet of Medical Things, connecting devices securely to hospital networks.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-900/60 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-teal-300 mb-4">Industries &gt; Healthcare</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Medical <span className="font-bold text-teal-400">Innovation</span></h1>
                        <p className="text-lg font-light text-teal-50 leading-relaxed mb-8">
                            Next-gen software for life-saving technologies.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-teal-600 text-white font-medium rounded-sm hover:bg-teal-700 transition-all text-sm uppercase tracking-wide">
                                Device Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6 text-slate-900">The Future of MedTech</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                Medical devices are no longer standalone hardware. They are intelligent, connected parts of a larger care ecosystem. Tech-Inovant helps MedTech leaders accelerate innovation while ensuring rigorous compliance.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                From smart implants to diagnostic imaging, we engineer software that enhances clinical outcomes and patient quality of life.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-teal-600" />
                                    <span className="text-slate-700 font-medium">Regulatory Excellence</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-teal-600" />
                                    <span className="text-slate-700 font-medium">IoMT Connectivity</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-teal-600" />
                                    <span className="text-slate-700 font-medium">Data Integrity</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1516549655169-df83a06745d6?w=800&q=80"
                                alt="Medical Technology"
                                className="rounded-sm shadow-sm"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">100%</div>
                                <div className="text-xs uppercase tracking-wider">Compliance</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4 text-slate-900">Engineering Services</h2>
                        <div className="w-20 h-1 bg-teal-600"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white border border-slate-200 hover:border-teal-400 transition-colors p-0 overflow-hidden group">
                                <div className="h-48 overflow-hidden relative">
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-teal-600 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-teal-700 hover:text-teal-900 tracking-wide uppercase">
                                        Learn More <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Metrics */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center text-slate-900">Clinical Impact</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Diagnostic Giant', stat: '2x Speed', desc: 'Faster image processing with edge AI.' },
                            { title: 'Wearable Startup', stat: '510(k)', desc: 'Cleared in record time with our documentation.' },
                            { title: 'Surgical Robotics', stat: '0 Failures', desc: 'In 10,000+ simulated procedures.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-teal-500">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-teal-800 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">MedTech FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-50"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Build the Future of Care</h3>
                            <p className="text-slate-600 mb-8">Connect with our MedTech experts.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-teal-500 outline-none transition-colors" placeholder="Dr. Smith" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-teal-500 outline-none transition-colors" placeholder="MedDevice Inc." />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-teal-500 outline-none transition-colors" placeholder="smith@meddevice.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Interest</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-teal-500 outline-none transition-colors text-slate-700">
                                        <option>Device Software</option>
                                        <option>Regulatory Compliance</option>
                                        <option>Cloud Integration</option>
                                        <option>Cybersecurity</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-teal-600 text-white font-bold py-4 rounded-sm hover:bg-teal-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Contact Experts
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-12 bg-slate-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-light mb-6">Accelerate Your Product</h2>
                    <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                        From concept to clearance, we are your engineering partner.
                    </p>
                    <button className="px-8 py-3 bg-white text-slate-900 font-bold uppercase tracking-wide hover:bg-teal-50 transition-colors">
                        Start Your Project
                    </button>
                </div>
            </section>
        </div>
    );
};

export default MedicalDevicesPage;
